import { FC, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ScopedSummarySection } from 'src/charts/SummarySection'
import { Skeleton } from 'src/components/Skeleton'
import { StatusModal } from 'src/components/StatusModal'
import {
  AI_LISTENING_BRAND_PROFILE_ROUTE,
  AI_LISTENING_BRAND_ROUTE,
  AI_LISTENING_COMPETITORS_ROUTE,
  AI_LISTENING_COMPETITOR_NEW_ROUTE,
  AI_LISTENING_COMPETITOR_ROUTE,
} from 'src/constants/routes'
import {
  useAiListeningDateRange,
  useAiListeningLink,
} from 'src/hooks/useAiListeningDateRange'
import { useDeleteAiListeningCompetitorMutation } from 'src/reactQuery/useDeleteAiListeningCompetitorMutation'
import { useGetAiListeningProfileQuery } from 'src/reactQuery/useGetAiListeningProfileQuery'
import styles from './competitors.module.scss'
import { AddPromptFlow } from '../shared/AddPrompt'
import { BrandDashboardView } from '../shared/Dashboard'
import { EntitySelect } from '../shared/EntitySelect'
import { AiListeningPage, PageBreadcrumb, PageTitleRow } from '../shared/Layout'
import { ProfileHeader, ProfileTabs } from '../shared/ProfileHeader'
import { SiteAuditCard } from '../shared/SiteAudit'
import { StateCard } from '../shared/StateCard'

type RemoveStep = 'confirm' | 'failed' | 'idle'

// /ai-listening/brand/:profileId/competitors[/:competitorId] — a competitor's
// dashboard, computed from the brand's own prompts seen from the
// competitor's side.
export const CompetitorsPage: FC = () => {
  const { competitorId, profileId = '' } = useParams<{
    competitorId?: string
    profileId: string
  }>()
  const navigate = useNavigate()
  const link = useAiListeningLink()
  const range = useAiListeningDateRange()
  const [removeStep, setRemoveStep] = useState<RemoveStep>('idle')
  const [isAddingPrompt, setIsAddingPrompt] = useState(false)

  const {
    data: profile,
    isError,
    isLoading,
  } = useGetAiListeningProfileQuery(profileId)
  const competitors = profile?.competitors ?? []
  const competitor = competitors.find((item) => item._id === competitorId)

  const removeMutation = useDeleteAiListeningCompetitorMutation({
    onError: () => setRemoveStep('failed'),
    onSuccess: () => {
      toast.success(`${competitor?.brandName ?? 'Competitor'} removed`)
      setRemoveStep('idle')
      navigate(link(AI_LISTENING_COMPETITORS_ROUTE, { profileId }), {
        replace: true,
      })
    },
  })

  const removeCompetitor = () => {
    if (competitorId && !removeMutation.isPending) {
      removeMutation.mutate({ competitorId, profileId })
    }
  }

  const addCompetitor = () =>
    navigate(link(AI_LISTENING_COMPETITOR_NEW_ROUTE, { profileId }))

  if (isLoading) {
    return (
      <AiListeningPage>
        <Skeleton height={20} width={260} />
        <Skeleton height={52} radius={10} width={420} />
        <Skeleton height={400} radius={20} width="100%" />
      </AiListeningPage>
    )
  }

  if (isError || !profile) {
    return <Navigate replace to={link(AI_LISTENING_BRAND_ROUTE)} />
  }

  // No competitor picked (or it was removed): open the first one.
  if (!competitor && competitors.length > 0) {
    return (
      <Navigate
        replace
        to={link(AI_LISTENING_COMPETITOR_ROUTE, {
          competitorId: competitors[0]._id,
          profileId,
        })}
      />
    )
  }

  if (!competitor) {
    return (
      <AiListeningPage>
        <PageBreadcrumb
          backTo={link(AI_LISTENING_BRAND_PROFILE_ROUTE, { profileId })}
          items={['AI Listening', 'Competitor']}
        />
        <PageTitleRow title="Competitor AI Visibility">
          <ProfileTabs active="competitors" profileId={profileId} />
        </PageTitleRow>
        <StateCard
          action={{ label: 'Add Competitor', onClick: addCompetitor }}
          description={
            <>
              Add a competitor to see how they appear in ChatGPT, Gemini,
              Perplexity and
              <br />
              Claude, and compare them with your brand.
            </>
          }
          icon="competitor"
          title="No Competitors Added Yet"
        />
      </AiListeningPage>
    )
  }

  return (
    <AiListeningPage>
      <PageBreadcrumb
        backTo={link(AI_LISTENING_BRAND_PROFILE_ROUTE, { profileId })}
        items={['AI Listening', 'Brand Profiles', 'Competitor']}
      />
      <PageTitleRow title="Competitors" />
      <ProfileHeader
        profile={competitor}
        right={<ProfileTabs active="competitors" profileId={profileId} />}
      />

      <div className={styles.toolbar}>
        <div className={styles.toolbarPicker}>
          <span className={styles.toolbarLabel}>Competitor</span>
          <EntitySelect
            activeId={competitor._id}
            ariaLabel="Switch competitor"
            onSelect={(id) =>
              navigate(
                link(AI_LISTENING_COMPETITOR_ROUTE, {
                  competitorId: id,
                  profileId,
                })
              )
            }
            options={competitors.map((item) => ({
              id: item._id,
              label: item.brandName,
            }))}
          />
        </div>
        <div className={styles.toolbarActions}>
          <button
            className={styles.addButton}
            onClick={addCompetitor}
            type="button"
          >
            + Add competitor
          </button>
          <button
            className={styles.removeButton}
            onClick={() => setRemoveStep('confirm')}
            type="button"
          >
            Remove
          </button>
        </div>
      </div>

      {competitor.state === 'processing' && (
        <StateCard
          description={
            <>
              Hapttic is analyzing your competitors&apos; visibility across AI
              platforms to reveal how
              <br />
              they compare with your brand. Your competitive insights will be
              available soon.
            </>
          }
          icon="processing"
          title="Your Competitors Are Being Analyzed"
        />
      )}

      {competitor.state === 'empty' && (
        <StateCard
          action={{
            label: 'Add Prompt',
            onClick: () => setIsAddingPrompt(true),
          }}
          description={`Competitors are measured on ${profile.brandName}'s tracked prompts. Add a prompt to your brand profile to start comparing ${competitor.brandName} with your brand.`}
          icon="prompt"
          title="Add your first prompt"
        />
      )}

      {competitor.state === 'ready' && (
        <div className={styles.readyBody}>
          <BrandDashboardView
            profileId={profileId}
            range={range}
            subjectId={competitor._id}
          />
          <ScopedSummarySection
            scope={{
              id: profileId,
              kind: 'aiListeningProfile',
              subject: competitor._id,
            }}
          />
        </div>
      )}

      <SiteAuditCard
        brandName={competitor.brandName}
        profileId={profileId}
        subjectId={competitor._id}
      />

      <AddPromptFlow
        isOpen={isAddingPrompt}
        onClose={() => setIsAddingPrompt(false)}
        profile={profile}
      />

      <StatusModal
        doneLabel={removeMutation.isPending ? 'Removing…' : 'Remove competitor'}
        hideCountdown
        isOpen={removeStep === 'confirm'}
        message={`${competitor.brandName} will stop being tracked and disappear from Share of voice and comparisons. You can add it again later, but the history collected so far may not be restored.`}
        onClose={() => setRemoveStep('idle')}
        onSecondaryAction={removeCompetitor}
        secondaryLabel="Cancel"
        title={`Remove ${competitor.brandName}?`}
        type="delete"
      />
      <StatusModal
        doneLabel={removeMutation.isPending ? 'Trying…' : 'Try Again'}
        hideCountdown
        isOpen={removeStep === 'failed'}
        message="Something went wrong while removing. Please try again."
        onClose={() => setRemoveStep('idle')}
        onSecondaryAction={removeCompetitor}
        secondaryLabel="Discard"
        title="Failed to Remove Competitor"
        type="error"
      />
    </AiListeningPage>
  )
}
